/* eslint-env browser */
// Users › Customers (wave 3), in the look of the approved W2_Portal_Drivers
// and W2_Portal_Tables boards: the filters are chips with their counts
// (contacts invited or not, app-less customers), active filters show as
// removable chips in the table header next to the count, and the table is
// the shared modern DataTable (sticky header, zebra rows, Comfortable /
// Compact, Columns, Export, row actions, skeleton rows, an empty state that
// says why). Clicking a row opens the customer's details.
//
// Data, exactly as before (no new reads):
// - admin / finance / fleet admin: the full user list already loaded
//   (fetchUsersOnce -> staticusers), filtered in memory; counts are totals
//   over that list.
// - call-centre roles with settings.useCustomerOnDemand: the paginated feed
//   (fetchCustomersPage): the first page from the cache, then one indexed
//   server query per filter or search change (350 ms after typing stops),
//   counts from /customer_index_stats, "Load more" for the next page.
// Writes, as before: the approval switch writes ONLY `approved`
// (updateUserFields), Delete calls deleteUser after a dialog that names the
// customer, the app-link SMS goes through sendAppDownloadLinkSms.
// Role gates, as before: Edit, Documents and Delete for admins; the approval
// switch for admins with critical edits on; the platform / SMS columns and
// the app-less filter for admin and call-centre roles; exports for admins
// (CSV only with critical edits on); phone and email hidden in demo mode.
// Call-centre feedback (29 Oct 2026): a copy button beside each customer's
// number copies it without spaces (+251964540448) for MicroSIP; it never
// opens the row, and it is not there while numbers are hidden.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { api } from 'common';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import Avatar from '@mui/material/Avatar';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import PersonAddAltRoundedIcon from '@mui/icons-material/PersonAddAltRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import PhonelinkOffOutlinedIcon from '@mui/icons-material/PhonelinkOffOutlined';
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';
import SearchOffRoundedIcon from '@mui/icons-material/SearchOffRounded';
import { tokens as c, toneChipSx } from '../theme';
import { showToast, confirmDialog } from '../components/feedback';
import { formatAddisDateTime } from '../common/exportUtils';
import {
  DataTable,
  PageHeader,
  SearchField,
  ChipGroup,
  ToggleChip,
  ActiveFilters,
  CopyButton,
  StatusChip,
  ALL,
  formatPhoneDisplay,
  phoneMatches,
  calmCaps,
  usePageState,
  onPageStateReset,
} from '../components/modern';

// A customer is "app-less" (auto-created during a manual/phone booking and
// never installed the app) when their first or last name is the placeholder
// "Manual"/"Rider" used by AddBookings / share-trip auto-registration.
const isManualRider = (u) => {
  const names = [u?.firstName, u?.lastName].map((n) => (n || '').trim().toLowerCase());
  return names.includes('manual') || names.includes('rider');
};

const nameOf = (u) => `${(u && u.firstName) || ''} ${(u && u.lastName) || ''}`.trim();
const initialsOf = (u) =>
  [u && u.firstName, u && u.lastName]
    .map((n) => String(n || '').trim().charAt(0))
    .join('')
    .toUpperCase();

const NUMBER_FORMAT = new Intl.NumberFormat('en-US');
const fmt = (n) => NUMBER_FORMAT.format(n || 0);
const percent = (part, whole) => (whole ? Math.round((part / whole) * 100) : 0);

const STAFF_ROLES = ['admin', 'finance', 'callcenteragent', 'callcentersupervisor'];

// The query whose rows the customers slice holds (call-centre paged path,
// `${uid}|${filter}|${search}`): coming back from a customer with a kept
// filter reads nothing new.
let loadedQuery = null;
onPageStateReset(() => { loadedQuery = null; });

export default function Users() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const { deleteUser, fetchUsersOnce, fetchCustomersPage, sendAppDownloadLinkSms, updateUserFields } = api;
  const settings = useSelector((state) => state.settingsdata.settings);
  const staticusers = useSelector((state) => state.usersdata.staticusers);
  const usersLoading = useSelector((state) => state.usersdata.loading);
  const usersError = useSelector((state) => state.usersdata.error);
  const customersSlice = useSelector((state) => state.usersdata.customers);
  const auth = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const { state } = useLocation();

  // Paginated path: only for call-center roles AND only when the on-demand
  // feature is enabled. admin/finance/fleetadmin keep the full staticusers
  // load + in-memory filters + full CSV export.
  const role = auth.profile?.usertype;
  const usePaginated = settings?.useCustomerOnDemand === true && ['callcenteragent', 'callcentersupervisor', 'scout'].includes(role);
  const allowPrivate = !!settings.AllowCriticalEditsAdmin;
  const isAdmin = role === 'admin';
  const canSwitch = allowPrivate && isAdmin;
  const canSendAppLink = ['admin', 'callcenteragent', 'callcentersupervisor'].includes(role);

  // The search, filters and page are kept while staff open a customer and
  // come back (owner, 28 Oct: never reset a list's filters on the way back
  // from a detail page); components/modern/pageState.js.
  const [search, setSearch] = usePageState('customers', 'search', '');
  const [contacts, setContacts] = usePageState('customers', 'contacts', ALL); // 'all' | 'invited' | 'not_invited'
  const [appLess, setAppLess] = usePageState('customers', 'appless', false);
  const [page, setPage] = usePageState('customers', 'page', Number.isInteger(state?.pageNo) && state.pageNo >= 0 ? state.pageNo : 0);
  const [platformByRow, setPlatformByRow] = useState({}); // { [userId]: 'ANDROID' | 'IOS' }
  const [sendingId, setSendingId] = useState(null);
  // What this page changed since the list arrived: rows sent the app link
  // (patched at once) and rows deleted (gone at once).
  const [patches, setPatches] = useState({});
  const [removed, setRemoved] = useState([]);
  const searchDebounce = useRef(null);
  const didFilterInit = useRef(false);

  useEffect(() => {
    if (Number.isInteger(state?.pageNo) && state.pageNo >= 0) setPage(state.pageNo);
  }, [state]);

  // The paginated feed takes ONE server filter at a time (one indexed
  // query). Priority as before: app-less > invited > without invited.
  const serverFilter = () => {
    if (appLess) return 'appless';
    if (contacts === 'invited') return 'invited';
    if (contacts === 'not_invited') return 'without_invited';
    return null;
  };

  // Full list for admin / finance / fleet admin.
  useEffect(() => {
    if (!usePaginated) dispatch(fetchUsersOnce());
  }, [dispatch, fetchUsersOnce, usePaginated]);

  // Paginated feed: the first page (cache-eligible), then a refetch 350 ms
  // after a filter or the search changes.
  useEffect(() => {
    if (!usePaginated) return undefined;
    const query = { filter: serverFilter(), search: search.trim() || null };
    const queryKey = `${auth.profile?.uid}|${query.filter}|${query.search}`;
    if (!didFilterInit.current) {
      didFilterInit.current = true;
      // No filter kept: the default feed (cache first). A kept filter: its
      // rows, unless the store still holds them (back from a customer).
      if (!query.filter && !query.search) dispatch(fetchCustomersPage());
      else if (loadedQuery !== queryKey) {
        loadedQuery = queryKey;
        dispatch(fetchCustomersPage({ ...query, reset: true }));
      }
      return undefined;
    }
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => {
      loadedQuery = queryKey;
      dispatch(fetchCustomersPage({ ...query, reset: true }));
    }, 350);
    return () => {
      if (searchDebounce.current) clearTimeout(searchDebounce.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contacts, appLess, search, usePaginated]);

  const pagedRows = customersSlice && Array.isArray(customersSlice.rows) ? customersSlice.rows : null;

  // Every customer this person may see, before the filters.
  const customers = useMemo(() => {
    let base;
    if (usePaginated) base = pagedRows || [];
    else if (staticusers) {
      base = staticusers.filter(
        (user) =>
          user.usertype === 'customer' &&
          ((user.fleetadmin === auth.profile.uid && auth.profile.usertype === 'fleetadmin') || STAFF_ROLES.includes(auth.profile.usertype))
      );
    } else base = [];
    if (!removed.length && !Object.keys(patches).length) return base;
    return base.filter((u) => !removed.includes(u.id)).map((u) => (patches[u.id] ? { ...u, ...patches[u.id] } : u));
  }, [usePaginated, pagedRows, staticusers, auth.profile, removed, patches]);

  // Counts: totals over everything loaded (full list), or the server's
  // /customer_index_stats (paginated).
  const stats = useMemo(() => {
    if (usePaginated) {
      const s = customersSlice?.stats;
      return {
        total: s && typeof s.total === 'number' ? s.total : customers.length,
        withInvited: s && typeof s.withInvited === 'number' ? s.withInvited : 0,
        withoutInvited: s && typeof s.withoutInvited === 'number' ? s.withoutInvited : 0,
        appLess: s && typeof s.appLess === 'number' ? s.appLess : 0,
      };
    }
    const out = { total: customers.length, withInvited: 0, withoutInvited: 0, appLess: 0 };
    customers.forEach((u) => {
      if (u.hasInvitedContacts === true) out.withInvited += 1;
      else out.withoutInvited += 1;
      if (isManualRider(u)) out.appLess += 1;
    });
    return out;
  }, [usePaginated, customersSlice, customers]);

  // The rows shown: the server already filtered the paginated feed.
  const rows = useMemo(() => {
    if (usePaginated) return customers;
    let list = customers;
    if (contacts === 'invited') list = list.filter((u) => u.hasInvitedContacts === true);
    if (contacts === 'not_invited') list = list.filter((u) => !u.hasInvitedContacts);
    if (appLess) list = list.filter(isManualRider);
    return list;
  }, [usePaginated, customers, contacts, appLess]);

  // Loading and failed states. Paginated feed: before its first answer
  // hasMore is still the initial true, so an empty list is loading until
  // the server has replied.
  const listFailed = usePaginated
    ? !!customersSlice?.error && !customersSlice?.loading && !(pagedRows && pagedRows.length)
    : !staticusers && !usersLoading && !!(usersError && usersError.flag);
  const tableLoading = usePaginated
    ? !(pagedRows && pagedRows.length) && !listFailed && (!!customersSlice?.loading || customersSlice?.hasMore !== false)
    : !staticusers && !listFailed;
  const refreshing = usePaginated && !!customersSlice?.loading && !tableLoading;
  const retryLoad = () => {
    if (usePaginated) dispatch(fetchCustomersPage({ filter: serverFilter(), search: search.trim() || null, reset: true }));
    else dispatch(fetchUsersOnce());
  };

  const L = (key, dflt, opts) => t(key, { defaultValue: dflt, ...(opts || {}) });
  const hiddenText = t('hidden_demo');
  const phoneWord = t('phone', { defaultValue: 'Phone number' });
  const yesText = L('yes', 'Yes');
  const noText = L('no', 'No');
  const platformLabel = (p) => (p === 'IOS' ? L('ios', 'iOS') : p === 'ANDROID' ? L('android', 'Android') : '');

  const navState = { state: { pageNo: page } };
  const addCustomer = () => navigate('/users/edituser/customer', navState);
  const goDetails = (row) => navigate(`/users/customerdetails/${row.id}`, navState);
  const goEdit = (row) => navigate(`/users/edituser/customer/${row.id}`, navState);
  const goDocuments = (row) => navigate(`/users/userdocuments/${id}/${row.id}`, navState);

  // Writes ONLY the switch's field: spreading the cached row into editUser()
  // replaced the whole profile and wiped server-written fields (holiday
  // credit stamps, onboarding stamp…). updateUserFields patches the cache too.
  const handelApproved = (rowData) => {
    const patch = { approved: !rowData.approved };
    dispatch(updateUserFields(rowData.id, patch));
    return { ...rowData, ...patch };
  };

  // Send the chosen platform's app download link via SMS. Only on a
  // SUCCESSFUL send is the row marked sent (the Cloud Function persists it
  // server-side; here the row is patched at once). On failure the button
  // stays enabled and an error toast is shown.
  const handleSendAppLink = async (rowData, platform) => {
    if (!platform) {
      showToast({ kind: 'warning', message: L('app_link_pick_platform', 'Choose Android or iOS first.') });
      return;
    }
    if (!rowData?.mobile) {
      showToast({ kind: 'error', message: L('no_mobile_number', 'No mobile number on file.') });
      return;
    }
    setSendingId(rowData.id);
    try {
      await dispatch(sendAppDownloadLinkSms(rowData.id, rowData.mobile, platform, auth.profile.uid));
      const patch = { downloadLinkSentAt: Date.now(), downloadLinkPlatform: platform, downloadLinkSentBy: auth.profile.uid };
      setPatches((prev) => ({ ...prev, [rowData.id]: { ...(prev[rowData.id] || {}), ...patch } }));
      showToast({ kind: 'success', message: L('app_link_sms_sent', 'Download link sent.') });
    } catch (err) {
      const reason = err && err.message ? `: ${err.message}` : '';
      showToast({ kind: 'error', message: `${L('app_link_sms_failed', 'Could not send the SMS')}${reason}` });
    } finally {
      setSendingId(null);
    }
  };

  // Delete (admin only, as before) after a dialog that names the customer.
  const handleDelete = async (rowData) => {
    if (!settings.AllowCriticalEditsAdmin) {
      showToast({ kind: 'info', message: t('demo_mode') });
      return;
    }
    const name = nameOf(rowData) || formatPhoneDisplay(rowData.mobile);
    const ok = await confirmDialog({
      title: L('customers_delete_title', 'Delete customer {{name}}?', { name }),
      body: L('customers_delete_body', "This removes the customer's account. It cannot be undone."),
      confirmLabel: L('delete', 'Delete'),
      cancelLabel: L('confirm_cancel', 'Cancel'),
      destructive: true,
    });
    if (!ok) return;
    setRemoved((prev) => prev.concat([rowData.id]));
    Promise.resolve(dispatch(deleteUser(rowData.id))).then(
      () => showToast({ kind: 'success', message: L('customers_deleted', 'Customer deleted') }),
      () => {
        // The customer is still there: show the row again.
        setRemoved((prev) => prev.filter((x) => x !== rowData.id));
        showToast({ kind: 'error', message: L('customers_delete_failed', 'Could not delete the customer. Try again.') });
      }
    );
    dispatch(fetchUsersOnce());
  };

  // ── Filters ──────────────────────────────────────────────────────────
  const searchText = search.trim();
  const groupContacts = L('drivers_contacts', 'Contacts');
  const appLessLabel = L('customers_appless', 'App-less');
  const contactOptions = [
    { value: ALL, label: L('all', 'All') },
    {
      value: 'invited',
      label: L('drivers_invited', 'Invited contacts'),
      countText: tableLoading ? null : `${fmt(stats.withInvited)} · ${percent(stats.withInvited, stats.total)}%`,
    },
    {
      value: 'not_invited',
      label: L('drivers_not_invited', 'Not invited'),
      countText: tableLoading ? null : `${fmt(stats.withoutInvited)} · ${percent(stats.withoutInvited, stats.total)}%`,
    },
  ];
  const filterTokens = [];
  if (contacts !== ALL) {
    const opt = contactOptions.find((o) => o.value === contacts);
    filterTokens.push({ key: 'contacts', group: groupContacts, value: opt ? opt.label : contacts });
  }
  if (appLess) filterTokens.push({ key: 'appless', group: '', value: appLessLabel });
  const tokens = searchText ? filterTokens.concat([{ key: 'search', group: L('search', 'Search'), value: `“${searchText}”` }]) : filterTokens;
  const removeToken = (key) => {
    if (key === 'contacts') setContacts(ALL);
    else if (key === 'appless') setAppLess(false);
    else if (key === 'search') setSearch('');
  };
  const clearFilters = () => {
    setContacts(ALL);
    setAppLess(false);
  };
  const clearAll = () => {
    clearFilters();
    setSearch('');
  };

  // A new filter or search starts at the first page (the table resets on
  // its own search and sort).
  const filterSig = JSON.stringify([contacts, appLess]);
  const lastFilterSig = useRef(filterSig);
  useEffect(() => {
    if (lastFilterSig.current === filterSig) return;
    lastFilterSig.current = filterSig;
    setPage(0);
  }, [filterSig]);

  // Full list: name, and phone / email only when they may be shown (demo
  // mode never matches what it hides). Paginated: the server searched.
  const searchFn = (row, q, ql) => {
    if (nameOf(row).toLowerCase().includes(ql)) return true;
    if (!allowPrivate) return false;
    if (phoneMatches(row.mobile, q)) return true;
    return String(row.email || '').toLowerCase().includes(ql);
  };

  const countText = ({ shown, loading }) => {
    if (loading) return L('customers_loading', 'Loading customers…');
    const narrowed = filterTokens.length > 0 || !!searchText;
    if (!narrowed) {
      return t('customers_count', {
        count: stats.total,
        defaultValue_one: '{{count}} customer',
        defaultValue_other: '{{count}} customers',
        defaultValue: '{{count}} customers',
      });
    }
    return t('customers_count_shown', {
      count: stats.total,
      shown: fmt(usePaginated ? rows.length : shown),
      total: fmt(stats.total),
      defaultValue_one: 'Showing {{shown}} of {{total}} customer',
      defaultValue_other: 'Showing {{shown}} of {{total}} customers',
      defaultValue: 'Showing {{shown}} of {{total}} customers',
    });
  };

  // ── Columns ──────────────────────────────────────────────────────────
  const subText = { fontSize: 13, lineHeight: 1.45, color: c.INK_2 };
  const columns = [
    {
      id: 'customer',
      title: L('customer', 'Customer'),
      field: 'firstName',
      sortValue: (row) => nameOf(row),
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 200 }}>
          {row.profile_image ? (
            <Avatar src={row.profile_image} alt="" sx={{ width: 36, height: 36 }} />
          ) : (
            <Avatar aria-hidden="true" sx={{ width: 36, height: 36, bgcolor: c.SURFACE_ALT, color: c.INK_2, fontSize: 14, fontWeight: 600 }}>
              {initialsOf(row) || '?'}
            </Avatar>
          )}
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ fontSize: 15, fontWeight: 600, lineHeight: 1.4, color: c.INK, overflowWrap: 'anywhere' }}>
              {nameOf(row) || L('no_name', 'No name')}
            </Box>
            <Typography component="div" sx={{ ...subText, display: 'flex', alignItems: 'center', gap: 0.25, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
              {allowPrivate ? formatPhoneDisplay(row.mobile) : hiddenText}
              {allowPrivate ? <CopyButton value={row.mobile} phone what={phoneWord} sx={{ my: -0.5 }} /> : null}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      id: 'email',
      title: L('email', 'Email'),
      field: 'email',
      sortValue: (row) => (allowPrivate ? row.email || '' : ''),
      render: (row) => (
        <Box component="span" sx={{ overflowWrap: 'anywhere' }}>
          {allowPrivate ? row.email || '' : hiddenText}
        </Box>
      ),
    },
    { id: 'joined', title: L('createdAt', 'Joined'), field: 'createdAt', type: 'datetime', defaultSort: 'desc' },
    {
      id: 'approved',
      title: calmCaps(L('account_approve', 'Account approved')),
      field: 'approved',
      sortValue: (row) => (row.approved ? 1 : 0),
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <StatusChip
            tone={row.approved ? 'success' : 'neutral'}
            icon={row.approved ? <CheckRoundedIcon /> : <BlockRoundedIcon />}
            label={row.approved ? L('approved', 'Approved') : calmCaps(L('not_approved', 'Not approved'))}
          />
          {canSwitch ? (
            <Switch
              size="small"
              checked={!!row.approved}
              onChange={() => handelApproved(row)}
              inputProps={{ 'aria-label': `${calmCaps(t('account_approve'))}: ${nameOf(row)}` }}
            />
          ) : null}
        </Box>
      ),
    },
    {
      id: 'platform',
      title: L('platform', 'Platform'),
      field: 'downloadLinkPlatform',
      sortable: false,
      hidden: !canSendAppLink,
      render: (row) => {
        if (!isManualRider(row)) return <Box component="span" sx={{ color: c.TEXT_3 }}>—</Box>;
        const sent = !!row.downloadLinkSentAt;
        const value = platformByRow[row.id] || row.downloadLinkPlatform || '';
        return (
          <Select
            size="small"
            value={value}
            disabled={sent}
            displayEmpty
            onChange={(e) => setPlatformByRow((prev) => ({ ...prev, [row.id]: e.target.value }))}
            inputProps={{ 'aria-label': `${L('platform', 'Platform')}: ${nameOf(row)}` }}
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="" disabled>
              {L('select_platform', 'Select…')}
            </MenuItem>
            <MenuItem value="ANDROID">{L('android', 'Android')}</MenuItem>
            <MenuItem value="IOS">{L('ios', 'iOS')}</MenuItem>
          </Select>
        );
      },
    },
    {
      id: 'appLinkSms',
      title: L('app_link_sms', 'App link SMS'),
      sortable: false,
      searchable: false,
      hidden: !canSendAppLink,
      render: (row) => {
        if (!isManualRider(row)) return <Box component="span" sx={{ color: c.TEXT_3 }}>—</Box>;
        const sent = !!row.downloadLinkSentAt;
        const platform = platformByRow[row.id] || row.downloadLinkPlatform || '';
        const isSending = sendingId === row.id;
        if (sent) {
          return (
            <Tooltip title={formatAddisDateTime(row.downloadLinkSentAt)}>
              <Chip size="small" icon={<CheckRoundedIcon />} label={L('app_link_sent', 'Sent')} sx={{ ...toneChipSx('success'), height: 28, fontSize: 13 }} />
            </Tooltip>
          );
        }
        return (
          <Button
            variant="contained"
            size="small"
            disabled={!platform || isSending}
            onClick={() => handleSendAppLink(row, platform)}
            startIcon={isSending ? <CircularProgress size={14} color="inherit" /> : null}
            sx={{ whiteSpace: 'nowrap' }}
          >
            {isSending ? L('sending', 'Sending…') : L('send_app_link', 'Send SMS link')}
          </Button>
        );
      },
    },
  ];

  // The same export columns as before: the CSV has the picture's address,
  // the printed table does not; phone and email are hidden in demo mode.
  const exportColumns = [
    { title: t('createdAt'), field: 'createdAt', type: 'datetime' },
    { title: t('first_name'), field: 'firstName' },
    { title: t('last_name'), field: 'lastName' },
    { title: t('mobile'), value: (row) => (allowPrivate ? row.mobile || '' : hiddenText) },
    { title: t('email'), value: (row) => (allowPrivate ? row.email || '' : hiddenText) },
    { title: t('profile_image'), field: 'profile_image', print: false },
    { title: t('account_approve'), value: (row) => (row.approved ? yesText : noText) },
    ...(canSendAppLink
      ? [
          { title: L('platform', 'Platform'), value: (row) => platformLabel(row.downloadLinkPlatform) },
          { title: L('app_link_sms', 'App link SMS'), value: (row) => (row.downloadLinkSentAt ? formatAddisDateTime(row.downloadLinkSentAt) : '') },
        ]
      : []),
  ];

  // ── Row actions (role gates as before) ───────────────────────────────
  const actions = [
    ...(isAdmin
      ? [
          { key: 'edit', label: L('edit', 'Edit'), icon: <EditOutlinedIcon />, inline: true, onClick: goEdit },
          { key: 'documents', label: L('documents', 'Documents'), icon: <DescriptionOutlinedIcon />, inline: true, onClick: goDocuments },
        ]
      : []),
    { key: 'details', label: L('profile_page_subtitle', 'Profile details'), icon: <PersonOutlineRoundedIcon />, onClick: goDetails },
    ...(isAdmin
      ? [{ key: 'delete', label: L('customers_delete', 'Delete customer'), icon: <DeleteOutlineRoundedIcon />, destructive: true, onClick: handleDelete }]
      : []),
  ];

  const emptyState = ({ query }) => {
    if (filterTokens.length > 0) {
      return {
        icon: <FilterAltOffOutlinedIcon />,
        title: t('customers_empty_filtered', {
          count: filterTokens.length,
          defaultValue_one: 'No customers match this filter',
          defaultValue_other: 'No customers match these {{count}} filters',
          defaultValue: 'No customers match these {{count}} filters',
        }),
        body: query ? L('customers_empty_search', 'No customers match “{{query}}”', { query }) : undefined,
        actionLabel: L('clear_filters', 'Clear filters'),
        onAction: clearAll,
      };
    }
    if (query) {
      return {
        icon: <SearchOffRoundedIcon />,
        title: L('customers_empty_search', 'No customers match “{{query}}”', { query }),
        body: L('customers_empty_search_body', 'Try part of the name or the phone number.'),
        actionLabel: L('clear_search', 'Clear search'),
        onAction: () => setSearch(''),
      };
    }
    return {
      icon: <GroupsOutlinedIcon />,
      title: L('customers_empty_none', 'No customers yet'),
      body: L('customers_empty_none_body', 'Customers appear here after they sign up in the app, book through the call centre, or you add one.'),
      actionLabel: t('add_customer'),
      onAction: addCustomer,
    };
  };

  const searchHint = usePaginated
    ? L('search_name_or_phone', 'Search by name or phone…')
    : allowPrivate
      ? L('drivers_search_hint', 'Search name, phone or email')
      : L('customers_search_name', 'Search by name');
  const listTitle = calmCaps(t('riders_title', { defaultValue: 'Customers' }));

  return (
    <Box dir={i18n.dir()} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <PageHeader
        primaryAction={{ label: t('add_customer'), icon: <PersonAddAltRoundedIcon />, onClick: addCustomer, color: 'success' }}
      />

      <Paper sx={{ p: { xs: 2, md: 2.5 }, display: 'flex', alignItems: 'center', flexWrap: 'wrap', rowGap: 1.5, columnGap: 3 }}>
        <SearchField value={search} onChange={setSearch} placeholder={searchHint} />
        <ChipGroup label={groupContacts} value={contacts} options={contactOptions} onChange={setContacts} />
        {canSendAppLink ? (
          <Tooltip title={L('show_app_less_customers', 'Show only app-less customers (auto-created by a manual booking)')}>
            <span>
              <ToggleChip
                label={appLessLabel}
                icon={<PhonelinkOffOutlinedIcon />}
                countText={tableLoading ? null : fmt(stats.appLess)}
                selected={appLess}
                onClick={() => setAppLess((v) => !v)}
              />
            </span>
          </Tooltip>
        ) : null}
      </Paper>

      <DataTable
        title={listTitle}
        rows={rows}
        columns={columns}
        loading={tableLoading}
        refreshing={refreshing}
        error={listFailed ? { title: L('customers_load_failed', 'Could not load customers.'), onRetry: retryLoad } : null}
        countText={countText}
        headerContent={tokens.length ? <ActiveFilters tokens={tokens} onRemove={removeToken} onClearAll={clearAll} /> : null}
        search={false}
        searchText={search}
        onSearchChange={setSearch}
        serverSearch={usePaginated}
        searchFn={searchFn}
        resortKey={allowPrivate ? 'private' : 'demo'}
        onRowClick={goDetails}
        rowLabel={nameOf}
        actions={actions}
        page={page}
        onPageChange={setPage}
        prefsKey="customers"
        exportCsv={isAdmin && allowPrivate}
        exportPrint={isAdmin}
        exportColumns={exportColumns}
        exportFileName="customers"
        exportTitle={listTitle}
        emptyState={emptyState}
        minWidth={canSendAppLink ? 1080 : 860}
      />

      {usePaginated ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="body2" role="status" sx={{ color: c.INK_2 }}>
            {t('customers_loaded', {
              count: rows.length,
              defaultValue_one: '{{count}} customer loaded',
              defaultValue_other: '{{count}} customers loaded',
              defaultValue: '{{count}} customers loaded',
            })}
          </Typography>
          {customersSlice?.hasMore ? (
            <Button
              variant="outlined"
              color="neutral"
              disabled={customersSlice?.loading}
              startIcon={customersSlice?.loading ? <CircularProgress size={16} color="inherit" /> : null}
              onClick={() => dispatch(fetchCustomersPage({ cursor: customersSlice.cursor }))}
            >
              {customersSlice?.loading ? L('loading', 'Loading…') : L('load_more', 'Load more')}
            </Button>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
}
